import React from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { fg } from '@atlaskit/platform-feature-flags';
import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import { fireErrorAnalytics } from '@atlassian/error-handling';
import { getTraceIds } from '@atlassian/relay-traceid';

interface HubErrorBoundaryProps {
	children: React.ReactNode;
	fallback: React.ReactElement;
	id: string;
	operationNames?: string[];
	packageName: string;
	shouldForceError?: boolean;
}

const isFetchClientError = (err: Error) => {
	return (err as unknown as { status: number })?.status < 500;
};

export const HubErrorBoundary = ({
	children,
	packageName,
	id,
	fallback,
	shouldForceError,
	operationNames,
}: HubErrorBoundaryProps): React.JSX.Element => {
	const logError = useUFOReportError();

	const onError = (error: Error) => {
		if (isFetchClientError(error)) {
			// skip fetch client error e.g. 4xx
			return;
		}

		// react-ufo does not expose a dedicated traceId/attribute field, so the trace ID is
		// appended to the error message. fireErrorAnalytics additionally receives it as an attribute.
		let traceId: string | undefined;
		if (operationNames && fg('hub_error_analytics_trace_id')) {
			const traceIds = operationNames.flatMap((name) => getTraceIds(name));
			traceId = traceIds.find(Boolean);
		}

		// Fire additional error analytics
		try {
			// Log rUFO error metrics
			logError({
				name: error.name,
				errorMessage: traceId ? `${error.message} (traceId: ${traceId})` : error.message,
				errorStack: error.stack,
				forcedError: shouldForceError,
			});
			fireErrorAnalytics({
				error,
				meta: {
					id,
					packageName,
				},
				attributes: traceId ? { traceId } : undefined,
			});
		} catch (err) {
			// Added to silently fail Uncaught ReferenceError: __SERVER__ is not defined
			// eslint-disable-next-line no-console
			console.warn('Failed to fire error analytics:', err);
		}
	};

	return (
		<ErrorBoundary onError={onError} fallback={fallback}>
			{children}
		</ErrorBoundary>
	);
};
